import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { clearAttempt, retrieveAttemptAsync, selectAttempts } from '../../applicant/TestView/attemptSlice';
import {
  Box,
  Button,
  Collapse,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { fetchApplicantsAsync, selectApplicants } from './applicantsSlice';
import { clearScore, fetchScoresAsync, fetchScoresCSVAsync, selectScores } from './scoreSlice';
import { Score } from '../../../generated/api';
import { setPageName } from '../../../app/appSlice';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { clearAnswers, fetchAnswersAsync } from '../../applicant/TestView/answerSlice';
import AnswerSummary from './AnswerSummary';

const ScoreView = () => {
  const { attemptId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { attempt } = useAppSelector(selectAttempts);
  const { applicants } = useAppSelector(selectApplicants);
  const scoreState = useAppSelector(selectScores);

  const applicant = applicants.find((a) => a.id === attempt?.applicant);

  useEffect(() => {
    if (!attemptId || isNaN(Number(attemptId))) {
      navigate('/admin/applicants');
      return;
    }
    dispatch(setPageName('Score card'));
    dispatch(retrieveAttemptAsync(Number(attemptId)));
    dispatch(fetchScoresAsync(attemptId));
    dispatch(fetchAnswersAsync(attemptId));
    if (applicants.length === 0) {
      dispatch(fetchApplicantsAsync());
    }
    return () => {
      dispatch(clearAttempt());
      dispatch(clearScore());
      dispatch(clearAnswers());
    };
  }, []);

  const handleExport = () => {
    if (!attempt || !applicant) return;
    const fileName = `${applicant.firstName}_${applicant.lastName}_${attempt.endTime.toLocaleDateString('en-FI')}`;
    dispatch(fetchScoresCSVAsync({ id: attempt.id.toString(), fileName }));
  };

  if (!attempt || !applicant) return null;
  const Row = (score: Score) => {
    const [open, setOpen] = useState<boolean>(false);

    return (
      <>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
          <TableCell>
            {!!score.id && (
              <IconButton size="small" onClick={() => setOpen((prevState) => !prevState)}>
                {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            )}
          </TableCell>
          <TableCell>{score.category}</TableCell>
          <TableCell>{score.questionsAmount}</TableCell>
          <TableCell>{score.questionsAnsweredAmount}</TableCell>
          <TableCell>{score.correctAnsweredAmount}</TableCell>
          <TableCell>{`${score.correctPercentage}%`}</TableCell>
          <TableCell>{`${score.correctAnsweredPercentage}%`}</TableCell>
        </TableRow>
        {!!score.id && (
          <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
              <Collapse in={open} timeout="auto">
                <AnswerSummary categoryId={score.id} attemptId={attempt.id} />
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <Box padding={1}>
      <Container maxWidth="xl">
        <Paper>
          <Stack spacing={1} padding={1}>
            <Typography align="center" variant="h5">{`${applicant.firstName} ${applicant.lastName}`}</Typography>
            <Typography align="center">{attempt.endTime.toLocaleString('en-FI')}</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Category</TableCell>
                    <TableCell>#Questions</TableCell>
                    <TableCell>#Questions answered</TableCell>
                    <TableCell>#Correct answered</TableCell>
                    <TableCell>%Correct total</TableCell>
                    <TableCell>%Correct answered</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scoreState.scores.map((score) => (
                    <Row key={score.id} {...score} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center">
              <Button variant="contained" onClick={handleExport}>
                Export
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ScoreView;
